<script lang="ts">
	import { translateFilterName } from '$lib';
	import { m } from '$lib/paraglide/messages.js';
	import { getContext } from 'svelte';
	import { slide } from 'svelte/transition';

	import FilterDrawer from './FilterDrawer.svelte';
	import FilterValue from './FilterValue.svelte';
	import FilterProvider, { type TermsFilter } from './store.svelte';

	const { filter }: { filter: TermsFilter } = $props();

	const filterProvider = getContext<FilterProvider>('filter');

	let isShowingMore = $state(false);
	let isOpen = $state(filter.name === 'brand');
	let searchQuery = $state('');

	const isSearchable = $derived(filter.values.length > 5);
	const visibleChoices = $derived.by(() => {
		let choices = filterProvider.orderedFilters(filter, searchQuery, !isShowingMore);

		if (!isShowingMore) {
			choices = choices.filter(
				(choice) => filterProvider.isChecked(filter.name, choice.value) || choice.is_main,
			);
		}

		return choices;
	});
	const selectedValuesCount = $derived(filterProvider.getSelectedCount(filter.name));

	// Reset showingMore state on closing filter block
	$effect(() => {
		if (!isOpen) {
			isShowingMore = false;
		}
	});

	// Reset searchQuery on closing showingMore
	$effect(() => {
		if (!isShowingMore) {
			searchQuery = '';
		}
	});
</script>

{#if filter.values.length > 1}
	{@const name = translateFilterName(filter.name)}

	{#snippet icon()}
		<span class={['icon-[mdi--chevron-down] transition', { 'rotate-180': isOpen }]}></span>
	{/snippet}

	{#snippet selected()}
		{filterProvider.checkedFilters[filter.name].map(filterProvider.displayValue).join(', ')}
	{/snippet}

	<FilterDrawer
		{name}
		{icon}
		onclick={() => (isOpen = !isOpen)}
		selected={!isOpen && selectedValuesCount > 0 ? selected : undefined}
	>
		{#if isOpen}
			<div transition:slide class="flex flex-col gap-1 py-2">
				{#if isSearchable && isShowingMore}
					<div class="relative rounded border border-neutral-300">
						<input
							type="text"
							placeholder={m.search()}
							bind:value={searchQuery}
							class="w-full rounded px-2 py-1"
						/>
						<div class="absolute top-0 right-0 -z-10 flex h-full cursor-pointer items-center pr-3">
							<span class="icon-[mdi--magnify] size-6 text-neutral-400"></span>
						</div>
					</div>
				{/if}
				{#key isShowingMore}
					<ul
						class="{isShowingMore
							? 'max-h-72 overflow-y-auto'
							: 'overflow-y-hidden'} list-none divide-y divide-neutral-300"
					>
						{#each visibleChoices as value (value)}
							<li>
								<FilterValue name={filter.name} {isShowingMore} {searchQuery} {value} />
							</li>
						{/each}
					</ul>
				{/key}
				{#if isShowingMore || filter.values.length > visibleChoices.length}
					<button
						onclick={() => (isShowingMore = !isShowingMore)}
						class="inline-flex w-full items-center justify-center gap-1 rounded border border-neutral-800 bg-white py-2 text-xs font-semibold text-neutral-800 uppercase transition-colors hover:bg-neutral-800 hover:text-white"
					>
						<span>{isShowingMore ? m.show_less() : m.show_more()}</span>
						{#if !isShowingMore}
							<span>({filter.values.length})</span>
						{/if}
						<span class={isShowingMore ? 'icon-[mdi--chevron-up]' : 'icon-[mdi--chevron-down]'}
						></span>
					</button>
				{/if}
			</div>
		{/if}
	</FilterDrawer>
{/if}
